import { useCallback, useEffect, useRef, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { GET_SERIES_CLASSIFICATION } from 'modules/Apollo/queries';
import { useClinicalStepWriters } from '../clinicalStepStore';
import type { GetSeriesClassificationQuery } from 'generated/graphql';
import { flattenSeriesClassification } from '../../Viewer/StudiesBar/HangingProtocols/HPv4/hangingProtocolFormHelpers';

type ClassifyRequest = {
  viewportId: string;
  stepIndex: number;
  seriesDescription: string;
  seriesSmid: Nullable<string>;
  studyDescription?: string;
};

type PendingClassification = {
  stepIndex: number;
  abort: AbortController;
};

export function useClinicalViewportClassification() {
  // `pendingClassificationsCount` is a React state counter so components re-render when
  // the in-flight count changes. `pendingRef` is a ref (not state) because the Map must
  // be mutated synchronously in the same microtask as the fetch, and making it state
  // would cause a stale-closure read of the old Map on rapid consecutive classify() calls.
  const [pendingClassificationsCount, setPendingClassificationsCount] = useState(0);
  const pendingRef = useRef<Map<string, PendingClassification>>(new Map());
  const { applyServerCriteriaToViewport } = useClinicalStepWriters();

  const [fetchClassification] =
    useLazyQuery<GetSeriesClassificationQuery>(GET_SERIES_CLASSIFICATION);

  const classify = useCallback(
    (request: ClassifyRequest) => {
      const { viewportId, stepIndex, seriesDescription, seriesSmid, studyDescription } = request;
      // Without a smid, applyServerCriteriaToViewport can never find a match to write into (see
      // holdsSeries in clinicalStepStore.ts), so the fetch below would be wasted and would leave
      // pendingClassifications stuck incremented until it resolves into a no-op.
      if (seriesSmid == null) return;
      const key = `${stepIndex}:${viewportId}`;

      // Cancel existing classification for this viewport
      const existing = pendingRef.current.get(key);
      if (existing) {
        existing.abort.abort();
        pendingRef.current.delete(key);
        setPendingClassificationsCount((n) => Math.max(0, n - 1));
      }

      const abortController = new AbortController();
      pendingRef.current.set(key, { stepIndex, abort: abortController });
      setPendingClassificationsCount((n) => n + 1);

      fetchClassification({
        variables: {
          dicomSeriesDescription: seriesDescription,
          dicomStudyDescription: studyDescription ?? undefined,
        },
        context: {
          telemetry: { component: 'useClinicalViewportClassification' },
          fetchOptions: { signal: abortController.signal },
        },
      })
        .then((result) => {
          if (abortController.signal.aborted) return;

          const classification = result.data?.seriesClassification?.classification;
          if (classification) {
            const criteria = flattenSeriesClassification(classification).map((c) => ({
              category: c.category,
              value: c.value,
            }));

            // Both atoms, one operation. The classification is server-derived, so it must never be
            // what makes a step dirty -- and each atom is written only if it independently still
            // holds the series this fetch was issued for. See `applyServerCriteriaToViewport` for
            // what each of those two guards is protecting against; both were paid for in bugs.
            applyServerCriteriaToViewport({
              stepIndex,
              viewportId,
              criteria,
              seriesSmid,
            });
          }
        })
        .catch(() => {
          // Classification failed — viewport saves without criteria
        })
        .finally(() => {
          // Skip cleanup if cancelled or superseded — the replacement manages its own counter.
          if (!abortController.signal.aborted) {
            pendingRef.current.delete(key);
            setPendingClassificationsCount((n) => Math.max(0, n - 1));
          }
        });
    },
    [fetchClassification, applyServerCriteriaToViewport]
  );

  const cancelForStep = useCallback((stepIndex: number) => {
    for (const [key, pending] of pendingRef.current) {
      if (pending.stepIndex === stepIndex) {
        pending.abort.abort();
        pendingRef.current.delete(key);
        setPendingClassificationsCount((n) => Math.max(0, n - 1));
      }
    }
  }, []);

  const cancelAll = useCallback(() => {
    for (const [, pending] of pendingRef.current) {
      pending.abort.abort();
    }
    pendingRef.current.clear();
    setPendingClassificationsCount(0);
  }, []);

  // Cancel all in-flight requests on unmount to prevent stale Recoil writes
  // after the component has been removed from the tree.
  useEffect(() => () => cancelAll(), [cancelAll]);

  return {
    classify,
    pendingClassifications: pendingClassificationsCount,
    cancelForStep,
    cancelAll,
  };
}
